/**
 * Transport seam — ONE shape the typed §2 client speaks, TWO implementations:
 *
 *   • `httpTransport`  — fetch against `VITE_API_BASE_URL` (the real backend; the default).
 *   • `mockTransport`  — src/api/mock/mockAdapter.ts, an in-memory server that implements
 *                        API_CONTRACT_SV_v0.1 §2 byte-for-byte (status codes, envelopes, headers).
 *
 * Because the mock sits BELOW the typed client rather than beside it, every status-code branch,
 * header name and error envelope the app handles is exercised against the mock exactly as it will
 * be against the wire — that is what "the mock mirrors the contract byte-for-byte" means here.
 *
 * Selection is FAIL-CLOSED: the mock is used only when `VITE_MOCK === 'true'` (an explicit,
 * developer-local opt-in). An empty base URL does NOT fall back to the mock — the app targets the
 * real backend and fails honestly (the droom's fail-closed P1-D posture, carried forward verbatim).
 */

export type TransportMethod = 'GET' | 'POST' | 'PUT' | 'PATCH';

export interface TransportRequest {
  readonly method: TransportMethod;
  /** Path relative to the API base, e.g. `/api/v1/sv/capture/start`. */
  readonly path: string;
  readonly headers: Readonly<Record<string, string>>;
  /** JSON-serialisable body, or raw bytes for the octet-stream chunk PUT. */
  readonly body?: unknown | Uint8Array;
}

export interface TransportResponse {
  readonly status: number;
  /** Parsed JSON body, or null for 204 / empty / non-JSON. */
  readonly body: unknown;
  /**
   * `Retry-After` as delta-seconds (contract §14f: `503 SV_REDACTION_UNAVAILABLE` sends 30). Absent / unreadable (an
   * HTTP-date, or a cross-origin response that does not expose the header) ⇒ undefined.
   */
  readonly retryAfterS?: number;
}

export interface Transport {
  send(req: TransportRequest): Promise<TransportResponse>;
}

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

/** Explicit, fail-closed mock opt-in. Anything but the literal string 'true' is the real backend. */
export const isMockMode = (): boolean => import.meta.env.VITE_MOCK === 'true';

export const httpTransport: Transport = {
  async send(req) {
    const headers: Record<string, string> = { Accept: 'application/json', ...req.headers };
    const init: RequestInit = { method: req.method, headers };
    // ArrayBuffer.isView, not instanceof: typed arrays from another realm (workers, jsdom) are still bytes.
    if (ArrayBuffer.isView(req.body)) {
      headers['Content-Type'] = 'application/octet-stream';
      init.body = req.body as BodyInit;
    } else if (req.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(req.body);
    }
    const res = await fetch(`${API_BASE_URL}${req.path}`, init);
    const retryAfterS = parseRetryAfter(res.headers.get('Retry-After'));
    return { status: res.status, body: await readJson(res), ...(retryAfterS !== null ? { retryAfterS } : {}) };
  },
};

/** Delta-seconds only; an HTTP-date or anything else ⇒ null (the caller keeps its own floor). */
export function parseRetryAfter(value: string | null): number | null {
  if (!value || !/^\s*\d+\s*$/.test(value)) return null;
  return Number.parseInt(value, 10);
}

async function readJson(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      // A non-JSON body is never surfaced verbatim; the typed client falls back to its own copy.
      return { message: text.trim().startsWith('<') ? '' : text.trim().slice(0, 300) };
    }
  } catch {
    return null;
  }
}

let override: Transport | null = null;

/** Tests inject a transport here; production code never calls this. */
export function setTransportForTests(t: Transport | null): void {
  override = t;
}

/**
 * The transport in force. Resolved lazily so the mock module is only loaded when explicitly
 * enabled — the production bundle carries it (Vite cannot tree-shake on a runtime env read) but
 * never instantiates it.
 */
export async function resolveTransport(): Promise<Transport> {
  if (override) return override;
  if (isMockMode()) {
    const { mockTransport } = await import('./mock/mockAdapter');
    return mockTransport;
  }
  return httpTransport;
}
